/**
 * Настройки кабинета AQSI — схема владельца вида
 * `Platform.Infrastructure/Connectors/aqsi.settings.schema.json`
 * (`urn:resource-aqsi:settings:1.0-draft.3`). Проверка здесь — подсказка до
 * отправки; окончательно схему применяет Platform. Ключа кабинета в настройках
 * нет: он — секрет ресурса и уходит отдельным полем команды.
 *
 * `1.0-draft.3` = `1.0-draft.2` + необязательный блок `pos` (RP-A9): параметры
 * НАШИХ запросов к кассе. Новый кабинет форма заводит сразу с блоком; сохранённый
 * в `1.0-draft.2` остаётся как есть, пока блок не правят, — тогда версия
 * поднимается (в `1.0-draft.2` блок схема не пускает).
 */
export const AQSI_KIND = 'aqsi'
export const AQSI_TITLE = 'Касса AQSI'
export const AQSI_SCHEMA_VERSION = '1.0-draft.3'
/** Версия без блока `pos`: такие кабинеты уже сохранены и читаются без изменений. */
export const AQSI_SCHEMA_VERSION_WITHOUT_POS = '1.0-draft.2'

/** Права вида словами для реестра ресурсов (код показывается рядом). */
export const AQSI_CAPABILITY_LABELS: Readonly<Record<string, string>> = {
  'diagnostics.read': 'Диагностика кабинета',
  'catalog.read': 'Чтение каталога',
  'catalog.refresh': 'Обновление каталога',
  'prices.write': 'Изменение цен кабинета',
  'catalog.write': 'Правка карточки товара в кабинете',
  'catalog.apply.service': 'Доставка каталога службой',
  'devices.read': 'Список касс',
  'devices.refresh': 'Обновление списка касс',
  'register.read': 'Рабочие места',
  'cashiers.manage': 'Кассиры кабинета: ИНН',
  'session.open': 'Начать работу за кассой',
  'session.heartbeat': 'Держать связь окна кассы',
  'session.close': 'Закончить свою работу',
  'session.takeover': 'Забрать кассу у другого',
  'session.close-any': 'Освободить любую кассу',
  'session.journal.read': 'Журнал работы касс',
  'shift.adopt': 'Принять смену с кассы',
  'shift.open': 'Открыть смену',
  'shift.close': 'Закрыть смену',
  'operations.read': 'Состояние команд кассы',
  'operations.reconcile': 'Сверка команд кассы',
  'receipts.read': 'Чтение чеков ККМ',
  'receipts.journal.sync': 'Сверка журнала чеков',
  'delivery.read': 'Доставка каталога: просмотр',
  'delivery.reconcile': 'Доставка каталога: сверка',
  'sales.read': 'Просмотр продаж',
  'sales.manage': 'Продажа',
  'sales.cancel': 'Отмена продажи',
  'sales.price': 'Продажа: менять цену',
  'refunds.manage': 'Возврат по чеку',
  'sales.reconcile': 'Ручная сверка продаж',
  'sales.trace': 'Диагностика продаж',
  'resource.drain': 'Вывод кассы из работы',
  'resource.release': 'Освобождение кассы при выводе',
}

/** Что право открывает человеку (п. 10 замечаний 13.10.2026): одна фраза рядом с названием. */
export const AQSI_CAPABILITY_HINTS: Readonly<Record<keyof typeof AQSI_CAPABILITY_LABELS, string>> = {
  'diagnostics.read': 'Видеть профиль кабинета и проверять связь с AQSI.',
  'catalog.read': 'Видеть товары кабинета AQSI.',
  'catalog.refresh': 'Перечитать каталог кабинета из AQSI целиком.',
  'prices.write': 'Задавать цену товара в кабинете AQSI.',
  'catalog.write': 'Править карточку товара прямо в кабинете AQSI из окна товара источника.',
  'catalog.apply.service': 'Служба обмена кладёт в кабинет товары по правилам обмена; людям не выдаётся.',
  'devices.read': 'Видеть кассы кабинета.',
  'devices.refresh': 'Перечитать список касс из AQSI.',
  'register.read': 'Видеть рабочие места касс.',
  'cashiers.manage':
    'Видеть ИНН кассиров кабинета AQSI (персональные данные); без права видно только «ИНН указан» или нет. Заводить и менять кассиров этим же правом можно будет, когда служба научится их записывать.',
  'session.open': 'Сесть за кассу и начать работу в РМК.',
  'session.heartbeat': 'Окно кассы держит за собой кассу, пока открыто; нужно вместе с «Начать работу».',
  'session.close': 'Закончить свою работу за кассой.',
  'session.takeover': 'Забрать кассу у другого кассира.',
  'session.close-any': 'Освободить любую занятую кассу.',
  'session.journal.read': 'Видеть журнал, кто и когда работал за кассами.',
  'shift.adopt': 'Принять смену, уже открытую на кассе.',
  'shift.open': 'Открыть кассовую смену.',
  'shift.close': 'Закрыть кассовую смену.',
  'operations.read': 'Видеть, чем закончились команды кассе.',
  'operations.reconcile': 'Сверять команды кассы с AQSI, если ответ потерялся.',
  'receipts.read': 'Видеть чеки ККМ кабинета.',
  'receipts.journal.sync': 'Сверять журнал чеков с AQSI.',
  'delivery.read': 'Видеть, как каталог доставлен в кабинет.',
  'delivery.reconcile': 'Сверять доставку каталога с кабинетом.',
  'sales.read': 'Видеть продажи и их состояние.',
  'sales.manage': 'Продавать: собирать корзину и принимать оплату.',
  'sales.cancel': 'Отменять продажу до чека.',
  'sales.price': 'Менять цену товара в продаже.',
  'refunds.manage': 'Делать возврат по чеку.',
  'sales.reconcile': 'Вручную сверять продажу, исход которой не ясен.',
  'sales.trace':
    'Видеть, где в продаже ушло время чека (браузер, сеть, сервер, кабинет, касса), и включать подробный режим записи; продажа этого человека пишет в трассировку точки.',
  'resource.drain': 'Выводить кабинет из работы.',
  'resource.release': 'Освобождать кассы при выводе кабинета.',
}

/** Целые поля доставки: имя, подпись и границы схемы. */
export const AQSI_DELIVERY_NUMBERS = [
  { key: 'batchSize', label: 'Товаров в пакете', min: 1, max: 100 },
  { key: 'requestIntervalMilliseconds', label: 'Пауза между запросами, мс', min: 100, max: 60000 },
  { key: 'verificationConcurrency', label: 'Параллельных проверок', min: 1, max: 8 },
  { key: 'paymentMethodType', label: 'Признак способа расчёта', min: 1, max: 7 },
] as const

export const AQSI_DELIVERY_OPTIONAL_NUMBERS = [
  { key: 'taxCodeOverride', label: 'Код налога для всех товаров', min: 1, max: 10 },
  { key: 'defaultSubjectCode', label: 'Предмет расчёта по умолчанию', min: 1, max: 33 },
] as const

/**
 * Приставка ключа служебной группы новых карточек кабинета (`delivery.serviceGroupPrefix`): группа — приставка и rid источника. Не задана — служба
 * называет группы `rp-…`, как всегда. Ключ группы — ключ кабинета: латиница, цифры, «-» и «_», приставка до 32 знаков.
 */
export const AQSI_SERVICE_GROUP_PREFIX = {
  key: 'serviceGroupPrefix',
  label: 'Приставка служебной группы новых карточек (пусто — «rp-»)',
  pattern: /^[A-Za-z0-9_-]{1,32}$/,
} as const

export const AQSI_DELIVERY_FLAGS = [
  { key: 'allowMissingPrices', label: 'Отправлять товары без цены' },
  { key: 'preserveUnsupportedBarcodesAsProperties', label: 'Неподдерживаемые штрихкоды — в свойства товара' },
] as const

/** Параметры наших запросов к кассе (блок `pos`): значения, когда блока нет — как у службы (`AqsiPosSettings.Default`). */
export const AQSI_POS_DEFAULTS = {
  ttlMillis: 120_000,
  // Оплата по QR-коду СБП на терминале включена по умолчанию (решение владельца 23.10.2026: как в 1С — включено, можно выключить).
  paymentMode: 'sbp_with_card',
  printSlipCount: 0,
  skipPrinting: false,
} as const

export const AQSI_POS_NUMBERS = [
  { key: 'ttlMillis', label: 'Срок ожидания команды кассой, мс', min: 10_000, max: 600_000 },
] as const

export const AQSI_PAYMENT_MODES = [
  { value: 'card_only', label: 'Только картой' },
  { value: 'sbp_with_card', label: 'Картой или через СБП' },
  { value: 'sbp_only', label: 'Только через СБП' },
] as const

/** Галочка формы: включена — терминал предлагает карту или QR-код СБП (`sbp_with_card`), выключена — только карта (`card_only`). */
export const AQSI_POS_QR_LABEL = 'Принимать оплату по QR-коду (СБП) на терминале'

export const AQSI_POS_SKIP_PRINTING_LABEL = 'Не печатать бумажный чек, если он уходит покупателю'

export const AQSI_CODE_MAPS = [
  { key: 'taxCodes', label: 'Соответствие налогов' },
  { key: 'subjectCodes', label: 'Соответствие предметов расчёта' },
  { key: 'unitCodes', label: 'Соответствие единиц измерения' },
] as const

const INT32_MIN = -2147483648
const INT32_MAX = 2147483647

export interface AqsiSettingsIssue {
  field: string
  message: string
}

export function emptyAqsiSettings(): Record<string, unknown> {
  return {
    schemaVersion: AQSI_SCHEMA_VERSION,
    shopId: '',
    legalEntity: { registeredInn: '' },
    pos: { ...AQSI_POS_DEFAULTS },
    delivery: {
      batchSize: 50,
      requestIntervalMilliseconds: 1000,
      verificationConcurrency: 2,
      allowMissingPrices: false,
      paymentMethodType: 4,
      preserveUnsupportedBarcodesAsProperties: true,
      taxCodes: {},
      subjectCodes: {},
      unitCodes: {},
    },
  }
}

export function objectField(settings: Readonly<Record<string, unknown>>, name: string): Record<string, unknown> {
  const value = settings[name]
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

/**
 * Блок `pos` для показа: сохранённые поля как есть, недостающие — по умолчанию службы.
 * Очищенное поле (`null`) остаётся пустым и не подменяется умолчанием: иначе человек
 * не видел бы, что поле не заполнено, а проверка на него ругалась.
 */
export function posOf(settings: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return { ...AQSI_POS_DEFAULTS, ...objectField(settings, 'pos') }
}

/**
 * Правка одного поля блока `pos`. Блок собирается целиком (схема требует все четыре
 * поля), а версия поднимается до draft.3: в draft.2 блок схема не пускает.
 */
export function withPosField(
  settings: Readonly<Record<string, unknown>>,
  key: string,
  value: unknown,
): Record<string, unknown> {
  return { ...settings, schemaVersion: AQSI_SCHEMA_VERSION, pos: { ...posOf(settings), [key]: value } }
}

/** Соответствие кодов строками «ключ=число»: так его правит человек. */
export function formatCodeMap(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value !== 'object' || value === null) return ''
  return Object.entries(value as Record<string, unknown>)
    .map(([key, code]) => `${key}=${String(code)}`)
    .join('\n')
}

/**
 * Разобрать строки «ключ=число». Ошибка разбора возвращает исходный текст —
 * он остаётся в черновике строкой, и проверка не пустит его на сервер.
 */
export function parseCodeMap(text: string): Record<string, number> | string {
  // Без прототипа: «toString», «constructor», «__proto__» — обычные коды, а не поля объекта.
  const map = Object.create(null) as Record<string, number>
  for (const line of text.split('\n')) {
    if (line.trim() === '') continue
    const at = line.lastIndexOf('=')
    if (at <= 0) return text
    const key = line.slice(0, at).trim()
    const code = line.slice(at + 1).trim()
    if (key === '' || key.length > 256 || !/^-?\d+$/.test(code)) return text
    const number = Number(code)
    if (number < INT32_MIN || number > INT32_MAX || Object.hasOwn(map, key)) return text
    map[key] = number
  }
  return { ...map }
}

function integerIn(value: unknown, min: number, max: number): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
}

export function validateAqsiSettings(settings: Readonly<Record<string, unknown>>): AqsiSettingsIssue[] {
  const issues: AqsiSettingsIssue[] = []
  const shopId = settings.shopId
  if (typeof shopId !== 'string' || shopId.trim() === '') issues.push({ field: 'shopId', message: 'Магазин в кабинете: обязательно' })
  else if (shopId.length > 256) issues.push({ field: 'shopId', message: 'Магазин в кабинете: не длиннее 256 символов' })

  const inn = objectField(settings, 'legalEntity').registeredInn
  if (typeof inn !== 'string' || inn.trim() === '')
    issues.push({ field: 'legalEntity.registeredInn', message: 'ИНН организации: обязательно' })
  else if (inn.length > 32) issues.push({ field: 'legalEntity.registeredInn', message: 'ИНН организации: не длиннее 32 символов' })

  const delivery = objectField(settings, 'delivery')
  for (const { key, label, min, max } of AQSI_DELIVERY_NUMBERS) {
    if (!integerIn(delivery[key], min, max))
      issues.push({ field: `delivery.${key}`, message: `${label}: целое от ${min} до ${max}` })
  }
  for (const { key, label, min, max } of AQSI_DELIVERY_OPTIONAL_NUMBERS) {
    const value = delivery[key]
    if (value !== undefined && value !== null && !integerIn(value, min, max))
      issues.push({ field: `delivery.${key}`, message: `${label}: целое от ${min} до ${max} или пусто` })
  }
  const prefix = delivery[AQSI_SERVICE_GROUP_PREFIX.key]
  if (prefix !== undefined && (typeof prefix !== 'string' || !AQSI_SERVICE_GROUP_PREFIX.pattern.test(prefix)))
    issues.push({
      field: `delivery.${AQSI_SERVICE_GROUP_PREFIX.key}`,
      message: `${AQSI_SERVICE_GROUP_PREFIX.label}: латиница, цифры, «-» и «_», до 32 знаков, или пусто`,
    })
  for (const { key, label } of AQSI_DELIVERY_FLAGS) {
    if (typeof delivery[key] !== 'boolean') issues.push({ field: `delivery.${key}`, message: `${label}: да или нет` })
  }
  for (const { key, label } of AQSI_CODE_MAPS) {
    const value = delivery[key]
    if (typeof value !== 'object' || value === null || Array.isArray(value))
      issues.push({ field: `delivery.${key}`, message: `${label}: строки вида «код=число»` })
    else if (Object.keys(value).length > 256)
      issues.push({ field: `delivery.${key}`, message: `${label}: не больше 256 строк` })
  }
  validatePos(settings, issues)
  return issues
}

/** Блок `pos` необязателен, но если он есть — полный, в границах схемы и не в версии draft.2. */
function validatePos(settings: Readonly<Record<string, unknown>>, issues: AqsiSettingsIssue[]): void {
  if (settings.pos === undefined) return
  const pos = settings.pos
  if (typeof pos !== 'object' || pos === null || Array.isArray(pos)) {
    issues.push({ field: 'pos', message: 'Параметры работы кассы: заполните все поля' })
    return
  }
  if (settings.schemaVersion === AQSI_SCHEMA_VERSION_WITHOUT_POS)
    issues.push({ field: 'pos', message: `Параметры работы кассы: нужна версия настроек ${AQSI_SCHEMA_VERSION}` })
  const block = pos as Record<string, unknown>
  for (const { key, label, min, max } of [...AQSI_POS_NUMBERS, { key: 'printSlipCount', label: 'Копий слипа эквайринга', min: 0, max: 2 }]) {
    if (!integerIn(block[key], min, max)) issues.push({ field: `pos.${key}`, message: `${label}: целое от ${min} до ${max}` })
  }
  if (!AQSI_PAYMENT_MODES.some((mode) => mode.value === block.paymentMode))
    issues.push({ field: 'pos.paymentMode', message: 'Оплата на кассе: выберите один из способов' })
  if (typeof block.skipPrinting !== 'boolean')
    issues.push({ field: 'pos.skipPrinting', message: `${AQSI_POS_SKIP_PRINTING_LABEL}: да или нет` })
}
